import { UploadState } from '@ui5/webcomponents-react';
import dayjs from 'dayjs';
import { AttachmentErrorCode } from 'enum/AttachmentErrorCode';
import {
  GetAttachmentUrlDocument,
  useDeleteFileMutation,
  useGetMsGraphUserQuery,
  useUploadFileMutation
} from 'generated/graphql';
import { MutableRefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CombinedError, useClient } from 'urql';
import { AttachmentFile } from '../components/DisplayQuery/Attachments/Attachments';
import { GRAPHQL_UPLOAD_TYPE_HEADER, GraphQLUploadType } from 'enum/GraphQLUploadType';
import { modifyFetchOptionsHeaders } from 'utils/UrqlQueryModifier';
import { openWindow } from 'utils/windowOpen';

export type AttachmentToUpload = File & { id: string };

export interface UseFilesForAttachmentsProps {
  toastRef: MutableRefObject<any>;
  queryId: string;
  attachments: AttachmentFile[];
}

export const useFilesForAttachments = (props: UseFilesForAttachmentsProps) => {
  const EXTENSIONS_ICONS_ATTACHMENTS: Record<string, string> = {
    '.png': 'attachment-photo',
    '.jpg': 'attachment-photo',
    '.jpeg': 'attachment-photo',
    '.zip': 'attachment-zip-file',
    '.pdf': 'pdf-attachment'
  };

  const client = useClient();
  const { t } = useTranslation();
  const [{ data }] = useGetMsGraphUserQuery();
  const { queryId, attachments: attachmentsProp, toastRef } = props;

  const [attachmentsList, setAttachmentsList] = useState<AttachmentFile[]>(
    attachmentsProp as AttachmentFile[]
  );
  const [toastMessage, setToastMessage] = useState('');

  const [, uploadFile] = useUploadFileMutation();
  const [, deleteFile] = useDeleteFileMutation();

  const handleDragAndDrop = (event: any) => {
    event.preventDefault();
    const { files } = event.dataTransfer;
    return handleUpload(files);
  };

  const handleFileUpload = async (event: CustomEvent) => {
    const { files } = event.target as any;
    await handleUpload(files);
  };

  const handleUpload = async (files: any) => {
    const fileList: AttachmentToUpload[] = Array.from(files);
    const attachments = fileList.map(mapFileToAttachment);
    setAttachmentsList((prev) => [...prev, ...attachments]);
    await uploadFiles(
      fileList,
      attachments.map((att) => att.id)
    );
  };

  const mapFileToAttachment = (file: AttachmentToUpload): AttachmentFile => {
    const { user: msalUser } = data!;

    const givenName = msalUser?.givenName || msalUser?.userPrincipalName || '';
    return {
      id: Math.random().toString(),
      name: file.name,
      createdAt: dayjs().toString(),
      uploadedBy: {
        firstName: givenName,
        lastName: msalUser?.surname || '',
        displayName: `${givenName} ${msalUser?.surname}`
      },
      uploadState: UploadState.Uploading
    };
  };

  const setErrorMessage = (combinedError?: CombinedError) => {
    const errorMessage = combinedError?.message.replace('[GraphQL] ', '');
    const [errorCode] = errorMessage!.split(' ').map((str) => str.trim());
    let message = '';
    switch (errorCode) {
      case AttachmentErrorCode.FILE_SIZE_EXCEEDED:
        message = t('attachment-file-size-exceeded');
        break;
      case AttachmentErrorCode.QUERY_NOT_FOUND:
        message = t('attachment-query-not-found');
        break;
      case AttachmentErrorCode.UPLOAD_ERROR:
        message = t('attachment-file-upload-error');
        break;
      case AttachmentErrorCode.SAVE_ATTACHMENT_ERROR:
        message = t('attachment-save-error');
        break;
      case AttachmentErrorCode.INVALID_MIME_TYPE:
        message = t('attachment-invalid-mime-type', {
          'accepted-mimetypes': Object.keys(EXTENSIONS_ICONS_ATTACHMENTS).join(', ')
        });
        break;
      case AttachmentErrorCode.MALWARE_DETECTED:
        message = t('attachment-malware-detected');
        break;
    }
    setToastMessage(message);
  };

  const uploadFiles = async (files: AttachmentToUpload[], ids: string[]) => {
    files.forEach(async (file, i) => {
      const fetchOptions = modifyFetchOptionsHeaders(client.fetchOptions, {
        [GRAPHQL_UPLOAD_TYPE_HEADER]: GraphQLUploadType.ATTACHMENT
      });
      const result = await uploadFile({ id: queryId as string, file }, { fetchOptions });
      if (result.error) {
        setErrorMessage(result.error);
        if (toastRef.current) {
          toastRef.current.show();
        }
        setAttachmentsList((prev) => prev.filter((attachment) => attachment.id !== ids[i]));
      } else {
        modifyUpload(ids[i], (att) => {
          att.id = result.data?.uploadFile.attachment?.id || att.id;
          att.uploadState = UploadState.Complete;
          return att;
        });
      }
    });
  };

  const modifyUpload = (id: string, modifier: (att: AttachmentFile) => AttachmentFile) => {
    setAttachmentsList((prev) => {
      const idx = prev.findIndex((attachment) => attachment.id === id);
      if (idx >= 0) {
        prev[idx] = modifier(prev[idx]);
      }
      return [...prev];
    });
  };

  const handleFileDeleted = async (event: CustomEvent) => {
    return deleteAttachmentFromServer(event.detail.item.id);
  };

  const removeAttachmentFromState = (attachmentListId: string) => {
    const filteredAttachments = attachmentsList.filter(
      (attachment) => attachment.id !== attachmentListId
    );
    setAttachmentsList(filteredAttachments);
  };

  const deleteAttachmentFromServer = async (attachmentId: string) => {
    return deleteFile({ id: attachmentId })
      .then((result) => {
        if (result.error) {
          setToastMessage(t('display-query-file-not-deleted'));
        } else {
          setToastMessage(t('display-query-file-deleted'));
          removeAttachmentFromState(attachmentId);
        }
        toastRef.current?.show();
      })
      .catch(console.error);
  };

  const handleDownload = (event: CustomEvent) => {
    const { id } = event.target as HTMLInputElement;
    return downloadFile(id);
  };

  const downloadFile = async (attachmentId: string) => {
    const { data } = await client
      .query(GetAttachmentUrlDocument, { id: Number(attachmentId) })
      .toPromise();
    if (!data) {
      if (toastRef.current) {
        setToastMessage(t('display-query-file-not-downloaded'));
        toastRef.current.show();
      }
    } else {
      openWindow(data.attachment.url);
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = `.${fileName.split('.').pop()}`;
    return EXTENSIONS_ICONS_ATTACHMENTS[ext] || 'document';
  };

  return {
    handleFileUpload,
    handleFileDeleted,
    getFileIcon,
    handleDownload,
    handleDragAndDrop,
    toastMessage,
    attachmentsList,
    EXTENSIONS_ICONS: EXTENSIONS_ICONS_ATTACHMENTS
  };
};
